// src/userFlow/returnsPage.js
import { Link } from 'react-router-dom';
import { Package, Clock, ArrowLeft, Mail } from 'lucide-react';

export default function ReturnsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header (compact) */}
      <header className="bg-white shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
            SodaSips
          </Link>
          <nav className="hidden sm:flex items-center gap-6 text-sm">
            <Link to="/" className="text-gray-700 hover:text-orange-500">Home</Link>
            <Link to="/about" className="text-gray-700 hover:text-orange-500">About</Link>
            <Link to="/contact" className="text-gray-700 hover:text-orange-500">Contact</Link>
            <span className="text-gray-900 font-semibold">Returns</span>
          </nav>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-100 via-white to-red-100 opacity-60" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <p className="text-sm font-semibold text-orange-600 tracking-wide uppercase">Returns</p>
                <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight">
                  Returns & Refunds — Coming Soon
                </h1>
                <p className="mt-4 text-lg text-gray-600">
                  Returns will be a future enhancement for this project. We’re designing a simple,
                  transparent process for returns, exchanges, and refunds.
                </p>

                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-500" />
                    <span className="font-medium text-gray-900">Back to Home</span>
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition"
                  >
                    <Mail className="w-5 h-5 text-orange-600" />
                    <span className="font-medium text-gray-900">Contact Support</span>
                  </Link>
                </div>
              </div>

              {/* Nice “coming soon” card */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
                <div className="flex items-center gap-3">
                  <Package className="w-6 h-6 text-orange-600" />
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">What to expect</h2>
                </div>
                <ul className="mt-4 space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <Clock className="w-5 h-5 mt-0.5 text-gray-400" />
                    <span>
                      A self-service portal to request returns/exchanges and track status — <strong>coming soon</strong>.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="w-5 h-5 mt-0.5 text-gray-400" />
                    <span>
                      Clear timelines, prepaid labels (where applicable), and transparent refund policies.
                    </span>
                  </li>
                </ul>

                <div className="mt-6 rounded-xl bg-orange-50 border border-orange-100 p-4 text-sm text-orange-900">
                  In the meantime, if you need help with an order, please reach out via our{' '}
                  <Link to="/contact" className="underline font-medium">Contact page</Link> and include your order number.
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer — same as your other pages */}
      <footer className="bg-gray-800 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent mb-4">
                SodaSips
              </div>
              <p className="text-gray-400">
                Your one-stop shop for the world's best sodas and beverages. Fresh, fizzy, and delivered to your door.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-orange-400 transition-colors duration-200">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-orange-400 transition-colors duration-200">Contact</Link></li>
                <li><Link to="/returns" className="hover:text-orange-400 transition-colors duration-200">Returns</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Connect</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="https://facebook.com" className="hover:text-orange-400 transition-colors duration-200">Facebook</a></li>
                <li><a href="https://twitter.com" className="hover:text-orange-400 transition-colors duration-200">Twitter</a></li>
                <li><a href="https://instagram.com" className="hover:text-orange-400 transition-colors duration-200">Instagram</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 SodaSips. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
